import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { userSelector } from "slices/userSlice";
import Api from "services/api";
import InterpreterCancelActionModal from "./InterpreterCancelActionModal";
import toast from "react-hot-toast";
import { Spinner } from "reactstrap";
import echo from "services/echo";

const AdminNotificationToast = () => {
  const [notifications, setNotifications] = useState([]);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [pausedIds, setPausedIds] = useState(new Set());
  const timerDataRef = React.useRef({});

  const { user } = useSelector(userSelector);

  const clearNotificationTimer = (id) => {
    const timerData = timerDataRef.current[id];
    if (timerData?.timeoutId) {
      clearTimeout(timerData.timeoutId);
    }
    delete timerDataRef.current[id];
    setPausedIds((prev) => {
      if (!prev.has(id)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const startTimer = (id, duration) => {
    const startedAt = Date.now();
    const timeoutId = setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.notification_id !== id));
      clearNotificationTimer(id);
    }, duration);
    timerDataRef.current[id] = { timeoutId, remaining: duration, startedAt };
  };

  const pauseTimer = (id) => {
    const data = timerDataRef.current[id];
    if (!data) return;
    clearTimeout(data.timeoutId);
    const elapsed = Date.now() - data.startedAt;
    data.remaining = Math.max(0, data.remaining - elapsed);
    data.timeoutId = null;
  };

  const resumeTimer = (id) => {
    const data = timerDataRef.current[id];
    if (!data || data.timeoutId) return;
    data.startedAt = Date.now();
    data.timeoutId = setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.notification_id !== id));
      clearNotificationTimer(id);
    }, data.remaining);
  };

  // const fetchNotifications = async () => {
  //   try {
  //     const response = await Api.fetchAdminNotificationsUnread();

  //     if (response?.status >= 200 && response?.status < 300) {
  //       setNotifications(response.data.data || []);
  //     } else {
  //       setFetchError(
  //         response?.data?.message || "Failed to load admin notifications."
  //       );
  //     }
  //   } catch (error) {
  //     setFetchError(
  //       error?.message || "Failed to load admin notifications."
  //     );
  //   }
  // };

  const fetchNotifications = async () => {
    try {
      const response = await Api.fetchAdminNotificationsUnread();

      if (response?.status >= 200 && response?.status < 300) {
        const incoming = response.data.data || [];

        setNotifications((prev) => {
          const prevIds = new Set(prev.map((n) => n.notification_id));
          const incomingIds = new Set(incoming.map((n) => n.notification_id));
          const preserved = prev.filter((n) => prevIds.has(n.notification_id));

          const brandNew = incoming.filter((n) => !prevIds.has(n.notification_id));

          return [...preserved, ...brandNew];
        });
      } else {
        setFetchError(
          response?.data?.message || "Failed to load admin notifications."
        );
      }
    } catch (error) {
      setFetchError(error?.message || "Failed to load admin notifications.");
    }
  };

  // useEffect(() => {
  //   fetchNotifications();

  //   const interval = setInterval(() => {
  //     fetchNotifications();
  //   }, 30000);

  //   return () => clearInterval(interval);
  // }, []);


  useEffect(() => {
    notifications.forEach((notification) => {
      const id = notification.notification_id;
      if (!timerDataRef.current[id]) {
        startTimer(id, 5000);
      }
    });

    const currentIds = new Set(notifications.map((n) => n.notification_id));
    Object.keys(timerDataRef.current).forEach((idKey) => {
      const id = Number(idKey);
      if (!currentIds.has(id)) {
        clearNotificationTimer(id);
      }
    });
  }, [notifications]);

  useEffect(() => {
    const channel = echo.channel("notifications");

    channel.listen(".AdminNotificationCreated", (data) => {
      setNotifications((prev) => {
        if (prev.some((n) => n.notification_id === data.notification_id)) {
          return prev;
        }
        return [...prev, data];
      });
    });

    return () => {
      echo.leave("notifications");
    };
  }, []);

  
  useEffect(() => {
    const channel2 = echo.channel('check-interpreter-joined');
    channel2.listen('.interpreter-appointment-joined', (data) => {
      toast.error(data.message, {
        duration: 6000,
          position: 'top-right'
        });
      });

    return () => {
      echo.leave('check-interpreter-joined');
    };
  }, []);

  const handleTakeIt = async (notification) => {
    setLoading(true);
    clearNotificationTimer(notification.notification_id);
    setNotifications((prev) =>
      prev.filter((n) => n.notification_id !== notification.notification_id)
    );
    try {
      const response = await Api.acknowledgeAdminNotification(
        notification.notification_id
      );

      if (response?.status >= 200 && response?.status < 300) {
        setSelectedNotification(notification);
        setIsModalOpen(true);
      } else if (response?.status === 409) {
        setNotifications((prev) =>
          prev.filter(
            (n) => n.notification_id !== notification.notification_id
          )
        );
        toast.error(
          response?.data?.message ||
            "Someone else has taken this notification."
        );
      } else {
        toast.error(
          response?.data?.message ||
            "Unable to acknowledge notification."
        );
        setNotifications((prev) => [...prev, notification]);
      }
    } catch (error) {
      if (error?.response?.status === 409) {
        setNotifications((prev) =>
          prev.filter(
            (n) => n.notification_id !== notification.notification_id
          )
        );
        toast.error(
          error?.response?.data?.message ||
            "Someone else has taken this notification."
        );
      } else {
        toast.error(error?.message || "Unable to acknowledge notification.");
        setNotifications((prev) => [...prev, notification]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseToast = (notificationId) => {
    clearNotificationTimer(notificationId);
    setNotifications((prev) =>
      prev.filter((item) => item.notification_id !== notificationId)
    );
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedNotification(null);
  };

  const handleActionSuccess = ({ type, interpreterName }) => {
    if (type === "cancel") {
      toast.success("Appointment cancelled and client has been notified.");
    } else if (type === "assign") {
      toast.success(
        `Appointment successfully reassigned to ${interpreterName}.`
      );
    }

    if (selectedNotification) {
      clearNotificationTimer(selectedNotification.notification_id);
      setNotifications((prev) =>
        prev.filter(
          (item) =>
            item.notification_id !== selectedNotification.notification_id
        )
      );
    }

    handleModalClose();
  };

  return (
    <>
      <style>{`
        @keyframes notif-progress-shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    <div style={{ position: "fixed", right: 10, top: 10, zIndex: 2000, display: "flex", flexDirection: "column", gap: 12, alignItems: "flex-end" }}>
      {/* {fetchError && (
        <div className="alert alert-danger" style={{ minWidth: 320 }}>
          {fetchError}
        </div>
      )} */}
      {notifications.map((notification) => (
        <div key={notification.notification_id} className="card shadow" style={{ width: 340, borderRadius: 8 }}
          onMouseEnter={() => {
            pauseTimer(notification.notification_id);
            setPausedIds((prev) => new Set(prev).add(notification.notification_id));
          }}
          onMouseLeave={() => {
            resumeTimer(notification.notification_id);
            setPausedIds((prev) => {
              const next = new Set(prev);
              next.delete(notification.notification_id);
              return next;
            });
          }}
        >
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                <strong className="text-muted">Appointment #{notification.appointment_number}</strong>
                <div className="text-muted" style={{ fontSize: 13 }}>
                  Cancelled by {notification.cancelled_interpreter?.name || "Interpreter"}
                </div>
              </div>
              <button type="button" className="btn-close" aria-label="Close" onClick={() => handleCloseToast(notification.notification_id)} />
            </div>
            {/* <div className="mb-3">Appointment #{notification.appointment_number} has been cancelled by {notification.cancelled_interpreter?.name}</div> */}
            <button className="main_button w-100" block onClick={() => handleTakeIt(notification)} disabled={loading}>
              {loading ? <Spinner size="sm" /> : "I will take it"}
            </button>
            <div style={{ position: "relative", height: 3, background: "#eee", borderRadius: 2, marginTop: 10, overflow: "hidden" }}>
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  height: "100%",
                  width: "100%",
                  background: "#096171",
                  animation: "notif-progress-shrink 5s linear forwards",
                  animationPlayState: pausedIds.has(notification.notification_id) ? "paused" : "running",
                }}
              />
            </div>
          </div>
        </div>
      ))}
      {selectedNotification && (
        <InterpreterCancelActionModal
          isOpen={isModalOpen}
          notification={selectedNotification}
          onClose={handleModalClose}
          onSuccess={handleActionSuccess}
        />
      )}
    </div>
    </>
  );
};

export default AdminNotificationToast;
